import axios from 'axios';

import {TreeNode} from 'components';
import {ExternalIntegrationPurpose} from 'enums';
import {ClientDevice, CustomField, SessionActivityName, SessionScreenshot, SessionScreenshotsSettings} from 'interfaces';
import {
    AccountAiSettings, AccountSettingsFormFields, ActivitiesAiSettings, ActivityAiSettings, AssignmentData,
    DeepgramSettings, InteractiveChatItem,
    InternalLiveAgentSettings, MastraSettings, SecuritySettingsFormFields,
    SipSettings, WatsonxSettings, WhatsappSettings
} from 'types';

import {API_URL} from './constants';

const url = `${API_URL}/settings`;

const getUserInfoSchema = async (): Promise<AssignmentData<CustomField[]>[]> => {
    const res = await axios.post(`${url}/user-info-schema`);
    return res.data;
};

const getGuestUserInfoSchema = async (): Promise<AssignmentData<CustomField[]>[]> => {
    const res = await axios.post(`${url}/guest-user-info-schema`);
    return res.data;
};

const updateUserInfoSchema = async (
    data: {assignment: {groupId: string}, userInfoFields: CustomField[], controlOnlyInfoFields: CustomField[]}
) => {
    const res = await axios.patch(`${url}/user-info-schema`, data);
    return res.data;
};

const updateGuestUserInfoSchema = async (
    data: {assignment: {groupId: string}, userInfoFields: CustomField[], controlOnlyInfoFields: CustomField[]}
) => {
    const res = await axios.patch(`${url}/guest-user-info-schema`, data);
    return res.data;
};

const getStartSessionToken = async (): Promise<{[key in keyof typeof ClientDevice]: string}> => {
    const res = await axios.get(`${url}/client-create-session-tokens`);
    return res.data;
};

const updateStartSessionTokens = async (device: ClientDevice) => {
    const res = await axios.post(`${url}/client-create-session-tokens`, {device});
    return res.data;
};

const getCommonSettings = async (): Promise<AccountSettingsFormFields> => {
    const res = await axios.get(`${url}/common`);
    return res.data;
};

const saveCommonSettings = async (data: Partial<AccountSettingsFormFields>): Promise<void> => {
    const res = await axios.post(`${url}/common`, data);
    return res.data;
};

const getSecuritySettings = async (): Promise<SecuritySettingsFormFields> => {
    const res = await axios.get(`${url}/security`);
    return res.data;
};

const saveSecuritySettings = async (data: Partial<SecuritySettingsFormFields>): Promise<void> => {
    const res = await axios.post(`${url}/security`, data);
    return res.data;
};

const getScreenshots = async (): Promise<Record<string, SessionScreenshotsSettings>> => {
    const res = await axios.get(`${url}/screenshots`);
    return res.data;
};

const updateScreenshots = async (screenshots: SessionScreenshot[], groupId: string): Promise<SessionScreenshot[]> => {
    const res = await axios.post(`${url}/screenshots`, {screenshots, groupId});
    return res.data;
};

const getInteractiveChat = async (groupId: string): Promise<TreeNode<InteractiveChatItem>[]> => {
    const result = await axios.get(`${API_URL}/settings/interactive-chat`, {params: {groupId}});
    return result.data;
};

const getSIP = async (): Promise<SipSettings> => {
    const result = await axios.get(`${API_URL}/settings/sip`);
    return result.data;
};

const updateSIP = async (settings: Partial<SipSettings>): Promise<SipSettings> => {
    const result = await axios.post(`${API_URL}/settings/sip`, settings);
    return result.data;
};

const getWatsonx = async (purpose: ExternalIntegrationPurpose): Promise<WatsonxSettings> => {
    const result = await axios.get(`${API_URL}/settings/watsonx/${purpose}`);
    return result.data;
};

const updateWatsonx = async (purpose: ExternalIntegrationPurpose, settings: Partial<WatsonxSettings>): Promise<WatsonxSettings> => {
    const result = await axios.post(`${API_URL}/settings/watsonx/${purpose}`, settings);
    return result.data;
};

const getMastra = async (purpose: ExternalIntegrationPurpose, groupId?: string): Promise<MastraSettings> => {
    const result = await axios.get(`${API_URL}/settings/mastra/${purpose}`, {params: {groupId: groupId || undefined}});
    return result.data;
};

const updateMastra = async (
    purpose: ExternalIntegrationPurpose, settings: Partial<MastraSettings>, groupId?: string
): Promise<MastraSettings> => {
    const result = await axios.post(`${API_URL}/settings/mastra/${purpose}`, settings, {params: {groupId: groupId || undefined}});
    return result.data;
};

const getActivitiesAi = async (): Promise<ActivitiesAiSettings> => {
    const result = await axios.post(`${url}/get-activities-ai`);
    return result.data;
};

const updateActivityAi = async (activity: SessionActivityName, settings: ActivityAiSettings): Promise<ActivityAiSettings> => {
    const result = await axios.post(`${url}/update-activity-ai/${activity}`, settings);
    return result.data;
};

const updateAccountAi = async (settings: AccountAiSettings): Promise<AccountAiSettings> => {
    const result = await axios.post(`${url}/update-account-ai`, settings);
    return result.data;
};

const getDeepgram = async (purpose: ExternalIntegrationPurpose): Promise<DeepgramSettings> => {
    const result = await axios.get(`${API_URL}/settings/deepgram/${purpose}`);
    return result.data;
};

const updateDeepgram = async (purpose: ExternalIntegrationPurpose, settings: Partial<DeepgramSettings>): Promise<DeepgramSettings> => {
    const result = await axios.post(`${API_URL}/settings/deepgram/${purpose}`, settings);
    return result.data;
};

const getWhatsapp = async (purpose: ExternalIntegrationPurpose): Promise<WhatsappSettings> => {
    const result = await axios.get(`${API_URL}/settings/whatsapp/${purpose}`);
    return result.data;
};

const updateWhatsapp = async (purpose: ExternalIntegrationPurpose, settings: Partial<WhatsappSettings>): Promise<WhatsappSettings> => {
    const result = await axios.post(`${API_URL}/settings/whatsapp/${purpose}`, settings);
    return result.data;
};

const getInternalLiveAgent = async (purpose: ExternalIntegrationPurpose): Promise<InternalLiveAgentSettings> => {
    const result = await axios.get(`${API_URL}/settings/internal-live-agent/${purpose}`);
    return result.data;
};

const updateInternalLiveAgent = async (
    purpose: ExternalIntegrationPurpose, settings: Partial<InternalLiveAgentSettings>
): Promise<InternalLiveAgentSettings> => {
    const result = await axios.post(`${API_URL}/settings/internal-live-agent/${purpose}`, settings);
    return result.data;
};

const updateInteractiveChat = async (groupId: string, interactiveChat: TreeNode<InteractiveChatItem>[]) => {
    const result = await axios.post(`${API_URL}/settings/interactive-chat`, {groupId, interactiveChat});
    return result.data;
};

export const settingsApi = {
    getUserInfoSchema,
    getGuestUserInfoSchema,
    updateUserInfoSchema,
    updateGuestUserInfoSchema,
    getCommonSettings,
    saveCommonSettings,
    getSecuritySettings,
    saveSecuritySettings,
    getStartSessionToken,
    updateStartSessionTokens,
    getScreenshots,
    updateScreenshots,
    getInteractiveChat,
    updateInteractiveChat,
    getWatsonx,
    updateWatsonx,
    getMastra,
    updateMastra,
    getDeepgram,
    updateDeepgram,
    getActivitiesAi,
    updateActivityAi,
    updateAccountAi,
    getWhatsapp,
    updateWhatsapp,
    getInternalLiveAgent,
    updateInternalLiveAgent,
    getSIP,
    updateSIP
};